export const IGREJA_AREAS = [
  "IGREJA_NOTIFICACOES",
  "IGREJA_FINANCEIRO",
  "IGREJA_SITE",
  "IGREJA_LINKS",
  "IGREJA_LISTAGEM",
  "IGREJA_AGENDA",
  "IGREJA_INFORMATIVOS",
  "IGREJA_PERFIL",
  "IGREJA_MINISTERIOS",
  "IGREJA_ARMAZENAMENTO",
  "IGREJA_VENDAS",
] as const;

export type IgrejaArea = (typeof IGREJA_AREAS)[number];

export type IgrejaPermissaoUser = {
  Master?: boolean | null;
  logins?: Array<{ Id?: number }> | null;
  usuarioLogins?: Array<{
    tipoPermissao?: string | null;
    loginsId?: number | null;
    logins?: { Id?: number } | null;
  }> | null;
} | null | undefined;

function pertenceAoLogin(
  item: {
    loginsId?: number | null;
    logins?: { Id?: number } | null;
  },
  loginId: number,
): boolean {
  return item.loginsId === loginId || item.logins?.Id === loginId;
}

function tiposDoLogin(user: IgrejaPermissaoUser, loginId: number): string[] {
  if (!user || !loginId) return [];
  return (user.usuarioLogins ?? [])
    .filter((item) => item && pertenceAoLogin(item, loginId) && item.tipoPermissao)
    .map((item) => item!.tipoPermissao as string);
}

export function isIgrejaArea(tipo?: string | null): tipo is IgrejaArea {
  return IGREJA_AREAS.includes(tipo as IgrejaArea);
}

export function isIgrejaPermissaoModo(tipo?: string | null): boolean {
  return tipo === "IGREJA" || isIgrejaArea(tipo);
}

export function masterDaOrg(user: IgrejaPermissaoUser, _loginId?: number | null): boolean {
  return Boolean(user?.Master);
}

export function isIgrejaAdm(
  user: IgrejaPermissaoUser,
  loginId: number | null | undefined,
): boolean {
  if (!user) return false;
  if (user.Master) return true;
  if (!loginId) return false;
  return tiposDoLogin(user, loginId).includes("IGREJA");
}

export function hasIgrejaArea(
  user: IgrejaPermissaoUser,
  loginId: number | null | undefined,
  area: IgrejaArea,
): boolean {
  if (user?.Master) return true;
  if (!loginId) return false;
  if (isIgrejaAdm(user, loginId)) return true;
  return tiposDoLogin(user, loginId).includes(area);
}

export function hasOrgTipoPermissao(
  user: IgrejaPermissaoUser,
  loginId: number | null | undefined,
  ...tipos: string[]
): boolean {
  if (user?.Master) return true;
  if (!loginId) return false;
  const atuais = tiposDoLogin(user, loginId);
  return tipos.some((tipo) => atuais.includes(tipo));
}

export function canAccessIgrejaMode(
  user: IgrejaPermissaoUser,
  loginId: number | null | undefined,
): boolean {
  if (!user) return false;
  if (user.Master) return true;
  if (!loginId) return false;
  return tiposDoLogin(user, loginId).some((tipo) => isIgrejaPermissaoModo(tipo));
}

export function igrejaAreasDoUsuario(
  user: IgrejaPermissaoUser,
  loginId: number | null | undefined,
): IgrejaArea[] {
  if (user?.Master) return [...IGREJA_AREAS];
  if (!loginId) return [];
  if (isIgrejaAdm(user, loginId)) return [...IGREJA_AREAS];
  return IGREJA_AREAS.filter((area) => tiposDoLogin(user, loginId).includes(area));
}

export function parseIgrejaPermQuery(raw: string | null | undefined): {
  active: boolean;
  isAdm: boolean;
  areas: IgrejaArea[];
} {
  const tokens = (raw ?? "")
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
  if (tokens.length === 0) {
    return { active: false, isAdm: false, areas: [] };
  }
  const isAdm = tokens.includes("IGREJA");
  return {
    active: true,
    isAdm,
    areas: isAdm ? [...IGREJA_AREAS] : IGREJA_AREAS.filter((area) => tokens.includes(area)),
  };
}

export const IGREJA_AREA_ROTAS: Record<IgrejaArea, string> = {
  IGREJA_NOTIFICACOES: "/sistema/notificacoes-push",
  IGREJA_FINANCEIRO: "/sistema/financeiro",
  IGREJA_SITE: "/sistema/site-igreja",
  IGREJA_LINKS: "/sistema/links",
  IGREJA_LISTAGEM: "/sistema/listagem",
  IGREJA_AGENDA: "/sistema/agenda",
  IGREJA_INFORMATIVOS: "/sistema/informativos",
  IGREJA_PERFIL: "/sistema/organizacao",
  IGREJA_MINISTERIOS: "/sistema/ministerios",
  IGREJA_ARMAZENAMENTO: "/sistema/armazenamento",
  IGREJA_VENDAS: "/sistema/vendas",
};
